import React from "react";
import { Box, Typography } from "@material-ui/core";
import { Save, PanTool } from "@material-ui/icons";
import SectionCard from "./SectionCard";
import {
  RegistrationForm,
  ReviewForm,
  ProgressForm,
  AcceptanceForm,
  DepartmentLevelForm,
  SchoolLevelForm,
  NationalLevelForm,
  InternationalLevelForm,
  CouncilForm,
  ExportForm,
  DeleteForm,
  SettingForm,
} from "../StudentProjectForms";
import RequestStopForm from "./RequestStopForm";
import ConductConfirm from "./ConductConfirm";
import StudentResearchHistory from "./StudentResearchHistory";
import {
  STATUS_REGISTRATION_DRAFT,
  STATUS_REGISTRATION_SUBMITTED,
  STATUS_REGISTRATION_ACCEPTED,
  STATUS_REGISTRATION_ADJUST_REQUEST,
  STATUS_REGISTRATION_REJECTED,
  STATUS_CONDUCTED,
  STATUS_CONDUCTED_ADJUST_REQUEST,
  STATUS_CONDUCTED_REJECTED,
  STATUS_PROGRESS_DRAFT,
  STATUS_PROGRESS_SUBMITTED,
  STATUS_PROGRESS_ACCEPTED,
  STATUS_PROGRESS_ADJUST_REQUEST,
  STATUS_PROGRESS_REJECTED,
  STATUS_STOP_REQUESTED,
  STATUS_STOP_ACCEPTED,
  STATUS_STOP_REJECTED,
  STATUS_ACCEPTANCE_READY,
  STATUS_ACCEPTANCE_SUBMITTED,
  STATUS_COUNCIL_ASSIGNED,
  STATUS_COUNCIL_SCORED,
  STATUS_ACCEPTANCE_ACCEPTED,
  STATUS_ACCEPTANCE_ADJUST_REQUEST,
  STATUS_ACCEPTANCE_REJECTED,
  STATUS_SCHOOL_LEVEL_PENDING,
  STATUS_SCHOOL_LEVEL_DONE,
  STATUS_NATIONAL_LEVEL_PENDING,
  STATUS_NATIONAL_LEVEL_DONE,
  PROGRESS_REPORT_ITERATION,
  STATUS_INTERNATIONAL_LEVEL_PENDING,
  STATUS_INTERNATIONAL_LEVEL_DONE,
  STUDENT_RESEARCH_STATUS_LABEL,
  DURATION_TWO_YEAR,
} from "../../../constants/projectStatus";

// Hệ màu cho chip trạng thái.
const color = {
  primary:   "#243474",
  success:   "#60ae7d",
  secondary: "#f46423",
  error:     "#e00000",
  neutral:   "#808080",
};

// Màu chip theo loại status.
const COLOR_BY_STATUS = {
  [STATUS_REGISTRATION_DRAFT]:          color.primary,
  [STATUS_REGISTRATION_SUBMITTED]:      color.secondary,
  [STATUS_REGISTRATION_ACCEPTED]:       color.success,
  [STATUS_REGISTRATION_ADJUST_REQUEST]: color.secondary,
  [STATUS_REGISTRATION_REJECTED]:       color.error,

  [STATUS_CONDUCTED]:                color.success,
  [STATUS_CONDUCTED_ADJUST_REQUEST]: color.secondary,
  [STATUS_CONDUCTED_REJECTED]:       color.error,

  [STATUS_PROGRESS_DRAFT]:          color.primary,
  [STATUS_PROGRESS_SUBMITTED]:      color.secondary,
  [STATUS_PROGRESS_ACCEPTED]:       color.success,
  [STATUS_PROGRESS_ADJUST_REQUEST]: color.secondary,
  [STATUS_PROGRESS_REJECTED]:       color.error,

  [STATUS_STOP_REQUESTED]: color.secondary,
  [STATUS_STOP_ACCEPTED]:  color.neutral,
  [STATUS_STOP_REJECTED]:  color.error,

  [STATUS_ACCEPTANCE_READY]:          color.primary,
  [STATUS_ACCEPTANCE_SUBMITTED]:      color.secondary,
  [STATUS_COUNCIL_ASSIGNED]:          color.secondary,
  [STATUS_COUNCIL_SCORED]:            color.secondary,
  [STATUS_ACCEPTANCE_ACCEPTED]:       color.success,
  [STATUS_ACCEPTANCE_ADJUST_REQUEST]: color.secondary,
  [STATUS_ACCEPTANCE_REJECTED]:       color.error,

  [STATUS_SCHOOL_LEVEL_PENDING]:        color.secondary,
  [STATUS_SCHOOL_LEVEL_DONE]:           color.neutral,
  [STATUS_NATIONAL_LEVEL_PENDING]:      color.secondary,
  [STATUS_NATIONAL_LEVEL_DONE]:         color.neutral,
  [STATUS_INTERNATIONAL_LEVEL_PENDING]: color.secondary,
  [STATUS_INTERNATIONAL_LEVEL_DONE]:    color.success,
};

// ---------------------------------------------------------------------------
// Modal properties — mapping key mở modal → { title, form, actionButtons }
// ---------------------------------------------------------------------------
//
// Action mô hình:
//   - Các nút "Duyệt" của vòng duyệt do ReviewForm tự xử lý bên trong
//     (3 nút Duyệt / Yêu cầu chỉnh sửa / Loại) → actionButtons của modal
//     chỉ có [closeButton].
//   - Các form tự submit (RegistrationForm, ProgressForm, AcceptanceForm,
//     DepartmentLevelForm, CouncilForm, RequestStopForm) cũng tự có nút submit
//     → modal chỉ cần [closeButton].
//
// Điều này đơn giản hoá `StudentProjectModal.js` rất nhiều so với bản cũ.

const closeOnly = () => [{ closeButton: true }];

const modalPropertiesDict = {
  // ---- Đăng ký ----
  insert: {
    title: "Thêm đề tài NCKH sinh viên",
    size: "lg",
    form: (p) => <RegistrationForm mode="create" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  edit: {
    title: "Chỉnh sửa đề tài",
    size: "lg",
    form: (p) => <RegistrationForm detail={p.detail} mode="edit" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  detail: {
    title: "Chi tiết đề tài",
    size: "lg",
    form: (p) => <RegistrationForm detail={p.detail} mode="view" onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },

  // ---- Vòng duyệt đăng ký (trưởng khoa) ----
  reviewRegistration: {
    title: "Duyệt đăng ký đề tài",
    size: "lg",
    form: (p) => (
      <>
        <RegistrationForm detail={p.detail} mode="view" />
        <ReviewForm
          title="Kết luận vòng duyệt đăng ký"
          onReview={async (action, comment) => {
            const ok = await p.dispatch(p.actions.reviewRegistration(p.detail.id, action, comment));
            if (ok) p.handleClose();
            return ok;
          }}
        />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ---- Duyệt thực hiện (Phòng QLKH) ----
  conduct: {
    title: "Duyệt thực hiện đề tài",
    size: "sm",
    form: (p) => (
      <ConductConfirm
        detail={p.detail}
        onDone={p.handleClose}
      />
    ),
    actionButtons: closeOnly,
  },
  reviewConduct: {
    title: "Yêu cầu chỉnh sửa / Loại đề tài",
    size: "md",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <StudentResearchHistory detail={p.detail} />
        </Box>
        <ReviewForm
          title="Kết luận duyệt thực hiện"
          onReview={async (action, comment) => {
            const ok = await p.dispatch(p.actions.reviewConduct(p.detail.id, action, comment));
            if (ok) p.handleClose();
            return ok;
          }}
        />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ---- Báo cáo tiến độ (chỉ đề tài 2 năm) ----
  progress: {
    title: "Báo cáo tiến độ",
    size: "md",
    form: (p) => <ProgressForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  reviewProgress: {
    title: "Duyệt báo cáo tiến độ",
    size: "lg",
    form: (p) => {
      // Refactor 2026-08-26: chỉ còn 1 BCTĐ (iteration = 1) và người duyệt là
      // TRƯỞNG KHOA. Chỉ mở form kết luận khi BCTĐ đang ở status=submitted.
      const pending = (p.detail?.progressReports || []).some(
        (r) => r.iteration === PROGRESS_REPORT_ITERATION && r.status === "submitted"
      );
      return (
        <>
          <Box padding="16px 24px 0">
            <StudentResearchHistory detail={p.detail} />
          </Box>
          {!pending && (
            <Box padding="16px 24px">
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                Không có BCTĐ nào đang chờ duyệt.
              </Typography>
            </Box>
          )}
          {pending && (
            <>
              {/* Fix 2026-09-03: bỏ <ProgressForm readOnly /> ở đây — nội dung
                  BCTĐ đã được StudentResearchHistory render bên trên (kèm cả
                  thời điểm nộp + nhận xét), để cả hai thì khối "Báo cáo tiến
                  độ — Chờ duyệt" hiện LẶP 2 LẦN trong modal. */}
              <ReviewForm
                title="Kết luận báo cáo tiến độ"
                onReview={async (action, comment) => {
                  const ok = await p.dispatch(
                    p.actions.reviewProgressReport(
                      p.detail.id,
                      PROGRESS_REPORT_ITERATION,
                      action,
                      comment
                    )
                  );
                  if (ok) p.handleClose();
                  return ok;
                }}
              />
            </>
          )}
        </>
      );
    },
    actionButtons: closeOnly,
  },

  // ---- Xin dừng ----
  requestStop: {
    title: "Xin dừng thực hiện đề tài",
    size: "sm",
    form: (p) => <RequestStopForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  reviewStop: {
    title: "Duyệt đơn xin dừng",
    size: "md",
    form: (p) => <ReviewStopBlock detail={p.detail} dispatch={p.dispatch} actions={p.actions} handleClose={p.handleClose} />,
    actionButtons: closeOnly,
  },

  // ---- Hội đồng nghiệm thu ----
  // Refactor 2026-08-26: bỏ "councilProgress" (gán hội đồng duyệt tiến độ) —
  // BCTĐ giờ do Trưởng khoa duyệt trực tiếp, chỉ còn hội đồng nghiệm thu.
  councilFinal: {
    title: "Gán hội đồng nghiệm thu",
    size: "lg",
    // Render history (file tổng kết, BCTĐ...) phía trên để admin có context
    // trước khi quyết định gán hội đồng. Read-only.
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <StudentResearchHistory detail={p.detail} />
        </Box>
        <CouncilForm
          id="council-final-form"
          type="final"
          detail={p.detail}
          values={p.councilValues}
          setValues={p.setCouncilValues}
          handleClose={p.handleClose}
        />
      </>
    ),
    actionButtons: (p) => [
      { closeButton: true },
      {
        props: {
          form: "council-final-form",
          type: "submit",
          color: "primary",
          variant: "contained",
          startIcon: <Save />,
        },
        content: "Lưu hội đồng",
      },
    ],
  },

  // ---- Nghiệm thu ----
  acceptanceSubmit: {
    title: "Nộp báo cáo tổng kết",
    size: "md",
    form: (p) => <AcceptanceForm detail={p.detail} onDone={p.handleClose} />,
    actionButtons: closeOnly,
  },
  gradeDepartmentLevel: {
    title: "Nghiệm thu cấp Khoa",
    size: "md",
    // Trưởng khoa cần xem báo cáo tổng kết + toàn bộ lịch sử trước khi chấm
    // điểm + kết luận nghiệm thu + gán giải (refactor 2026-07-23, gộp 3 bước cũ).
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <StudentResearchHistory detail={p.detail} />
        </Box>
        <DepartmentLevelForm detail={p.detail} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },
  gradeSchoolLevel: {
    title: "Chấm báo cáo cấp Trường",
    size: "md",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <StudentResearchHistory detail={p.detail} />
        </Box>
        <SchoolLevelForm detail={p.detail} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },
  gradeNationalLevel: {
    title: "Cập nhật kết quả cấp Quốc gia",
    size: "md",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <StudentResearchHistory detail={p.detail} />
        </Box>
        <NationalLevelForm detail={p.detail} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },
  gradeInternationalLevel: {
    title: "Cập nhật kết quả cấp Quốc tế",
    size: "md",
    form: (p) => (
      <>
        <Box padding="16px 24px 0">
          <StudentResearchHistory detail={p.detail} />
        </Box>
        <InternationalLevelForm detail={p.detail} onDone={p.handleClose} />
      </>
    ),
    actionButtons: closeOnly,
  },

  // ---- Utilities ----
  delete: {
    title: "Xoá đề tài",
    size: "xs",
    form: (p) => (
      <DeleteForm
        handleClose={p.handleClose}
        title={p.detail?.name}
        deleteId={p.detail?.id}
        id="delete-form"
      />
    ),
    actionButtons: () => [],
  },
  setting: {
    title: "Cấu hình hệ thống",
    size: "sm",
    form: (p) => <SettingForm handleClose={p.handleClose} year={p.year} id="setting-form" />,
    actionButtons: () => [
      { closeButton: true },
      {
        props: {
          form: "setting-form",
          type: "submit",
          color: "primary",
          variant: "contained",
          startIcon: <Save />,
        },
        content: "Lưu cấu hình",
      },
    ],
  },
  export: {
    title: "Xuất file báo cáo",
    size: "md",
    form: (p) => <ExportForm {...p} id="export-form" />,
    actionButtons: closeOnly,
  },
};

// ReviewStopBlock — card hiển thị thông tin xin dừng (lý do + file đính kèm)
// để admin xem trước khi quyết định, kèm ReviewForm 3 nhánh.
// Thêm history phía trên (file 2026-05) để admin có context toàn lifecycle
// trước khi duyệt đơn dừng (vd BCTĐ đã nộp, hội đồng đã gán,...).
function ReviewStopBlock({ detail, dispatch, actions, handleClose }) {
  return (
    <Box padding="16px 24px">
      <StudentResearchHistory detail={detail} />
      <SectionCard icon={<PanTool />} title="Thông tin đơn xin dừng" marginTop={2}>
        <Typography variant="body2" gutterBottom>
          <b>Đề tài:</b> {detail?.name || "—"}
        </Typography>
        <Typography variant="body2" gutterBottom style={{ marginTop: 8 }}>
          <b>Lý do xin dừng:</b>
        </Typography>
        <Box
          padding={2}
          marginTop={1}
          bgcolor="#fafafa"
          border="solid 1px #e0e0e0"
          borderRadius={4}
        >
          <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
            {detail?.leaveReason || "(không có)"}
          </Typography>
        </Box>
        {detail?.leaveFileLink && (
          <Box marginTop={2}>
            <Typography variant="body2">
              <b>File đơn:</b>{" "}
              <a href={`/api/v1${detail.leaveFileLink}`} target="_blank" rel="noreferrer">
                Tải xuống
              </a>
            </Typography>
          </Box>
        )}
      </SectionCard>
      <ReviewForm
        title="Kết luận đơn xin dừng (Không duyệt = từ chối, GV tiếp tục thực hiện)"
        rejectLabel="Không duyệt"
        onReview={async (action, comment) => {
          const ok = await dispatch(actions.reviewStop(detail.id, action, comment));
          if (ok) handleClose();
          return ok;
        }}
      />
    </Box>
  );
}

// render — trả về properties theo key trong `open`.
// open là object dạng `{ <key>: true, ...extra }`; lấy key đầu tiên.
export const render = (open) => {
  if (!open) return null;
  for (const key of Object.keys(open)) {
    if (modalPropertiesDict[key]) return modalPropertiesDict[key];
  }
  return null;
};

// ---------------------------------------------------------------------------
// rowRender — map status hiện tại (last value) + type role → buttons.
// ---------------------------------------------------------------------------
//
// Output:
//   - status: array [{value, label, bgcolor}] để render chip trạng thái.
//   - buttons: array các action key để ActionButton render icon.
//
// Buttons phân theo role (type):
//   - personal: GV chủ trì đề tài
//   - mod:      Trưởng khoa
//   - admin:    Phòng QLKH
//   - council:  Thành viên hội đồng nghiệm thu (chấm điểm)

// getButtonsFor trả về buttons cho status cụ thể và type.
// Refactor 2026-08-26: mỗi đề tài SV chỉ nộp 1 BCTĐ (qua bảng con), do Trưởng
// khoa duyệt. Khi status=conducted:
//   - GV (personal): action "progress" để nộp BCTĐ.
//   - Trưởng khoa (mod): action "reviewProgress" khi có BCTĐ status=submitted
//     (đọc từ row.progressReports). Nếu không có → "detail".
const getButtonsFor = (status, type, _duration, row = {}) => {
  const hasSubmittedProgress = (row?.progressReports || []).some(
    (r) => r.status === "submitted"
  );

  // Bảng quyết định — đọc theo hàng, gọn hơn switch nhiều tầng.
  const rules = {
    // Refactor 2026-06-02: bỏ "delete" khỏi personal vì yêu cầu hạn chế chỉ
    // sudo-admin + admin Phòng QLKH mới được xoá đề tài (xem post-process
    // appendDelete trong StudentProjectTable). Personal vẫn edit được draft.
    [STATUS_REGISTRATION_DRAFT]: {
      personal: ["edit"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_REGISTRATION_SUBMITTED]: {
      personal: ["detail"],
      mod:      ["reviewRegistration"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_REGISTRATION_ADJUST_REQUEST]: {
      personal: ["edit"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_REGISTRATION_REJECTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_REGISTRATION_ACCEPTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["conduct", "reviewConduct"],
      council:  ["detail"],
    },
    [STATUS_CONDUCTED]: {
      // Refactor 2026-08-26: BCTĐ do TRƯỞNG KHOA (mod) duyệt, không còn là
      // việc của Phòng QLKH / hội đồng. GV thấy nút "progress" (nộp BCTĐ duy
      // nhất) + "requestStop"; Trưởng khoa thấy "reviewProgress" khi có BCTĐ
      // đang chờ duyệt.
      personal: ["progress", "requestStop"],
      mod:      hasSubmittedProgress ? ["reviewProgress"] : ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_CONDUCTED_ADJUST_REQUEST]: {
      personal: ["edit"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_CONDUCTED_REJECTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    // Nhánh báo cáo tiến độ — sống lại từ 2026-09-03 (BE đẩy trạng thái cha ở
    // cả 3 nhánh duyệt). "requestStop" giữ ở mọi bước GV còn đang thực hiện:
    // trước đây đề tài đứng ở `conducted` suốt giai đoạn BCTĐ nên GV luôn xin
    // dừng được — không được để thay đổi này lấy mất quyền đó.
    [STATUS_PROGRESS_DRAFT]: {
      personal: ["progress", "requestStop"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_PROGRESS_ADJUST_REQUEST]: {
      personal: ["progress", "requestStop"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_PROGRESS_SUBMITTED]: {
      personal: ["requestStop"],
      mod:      ["reviewProgress"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_PROGRESS_ACCEPTED]: {
      personal: ["acceptanceSubmit"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_PROGRESS_REJECTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_STOP_REQUESTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["reviewStop"],
      council:  ["detail"],
    },
    [STATUS_STOP_ACCEPTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_STOP_REJECTED]: {
      // BE đã revert về `conducted` khi reject đơn dừng, nên status này
      // hiếm khi xuất hiện. Giữ entry để xử lý data cũ; chỉ cho xem.
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_ACCEPTANCE_READY]: {
      personal: ["acceptanceSubmit"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_ACCEPTANCE_SUBMITTED]: {
      personal: ["detail"],
      mod:      ["councilFinal"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    // Refactor 2026-07-23 (luồng V2 — chuyển vòng nghiệm thu sang Trưởng khoa,
    // gộp chấm điểm + kết luận nghiệm thu + gán giải thành 1 bước):
    //   acceptance_submitted → mod: councilFinal (gán HĐ nghiệm thu)
    //   council_assigned     → mod: gradeDepartmentLevel (chấm điểm + kết luận +
    //                          gán giải + đề xuất báo cáo tại HNSV cấp Trường)
    // council_scored không còn là điểm dừng (gộp vào council_assigned).
    // Sau cấp Khoa (nếu đề xuất): Phòng QLKH tiếp quản 3 cấp Trường/Quốc gia/
    // Quốc tế (school/national/international_level_pending, admin:).
    [STATUS_COUNCIL_ASSIGNED]: {
      personal: ["detail"],
      mod:      ["gradeDepartmentLevel"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_ACCEPTANCE_ADJUST_REQUEST]: {
      personal: ["acceptanceSubmit"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_ACCEPTANCE_ACCEPTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },
    [STATUS_ACCEPTANCE_REJECTED]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["detail"],
      council:  ["detail"],
    },

    [STATUS_SCHOOL_LEVEL_PENDING]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["gradeSchoolLevel"],
      council:  ["detail"],
    },
    [STATUS_SCHOOL_LEVEL_DONE]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
    [STATUS_NATIONAL_LEVEL_PENDING]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["gradeNationalLevel"],
      council:  ["detail"],
    },
    [STATUS_NATIONAL_LEVEL_DONE]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
    [STATUS_INTERNATIONAL_LEVEL_PENDING]: {
      personal: ["detail"],
      mod:      ["detail"],
      admin:    ["gradeInternationalLevel"],
      council:  ["detail"],
    },
    [STATUS_INTERNATIONAL_LEVEL_DONE]: {
      personal: ["detail"], mod: ["detail"], admin: ["detail"], council: ["detail"],
    },
  };

  const typeRow = rules[status];
  // Fallback: mọi role đều có "detail" nếu status lạ.
  const buttons = typeRow?.[type] || ["detail"];
  // Prepend "detail" để mọi trạng thái đều có nút xem chi tiết (trừ khi đã có).
  return buttons.includes("detail") ? buttons : ["detail", ...buttons];
};

// rowRender — signature giữ tương thích với StudentProjectTable hiện tại.
//   Input:
//     - status: array string (đã split) hoặc array {value,label} cũ.
//     - type:   role (personal|mod|admin|council|conference)
//   Output:
//     - status: [{value, label, bgcolor}]
//     - buttons: [...action key]
export const rowRender = (status, type, row = {}) => {
  // Chuẩn hoá về string value cuối cùng.
  let lastValue = "";
  if (Array.isArray(status) && status.length > 0) {
    const last = status[status.length - 1];
    lastValue = typeof last === "object" ? last?.value : last;
  } else if (typeof status === "string") {
    const arr = status.split(",");
    lastValue = arr[arr.length - 1];
  }

  const label = STUDENT_RESEARCH_STATUS_LABEL[lastValue] || lastValue || "Không xác định";
  const bgcolor = COLOR_BY_STATUS[lastValue] || color.neutral;

  // "conference" role hiển thị giống "council" (xem bảng nghiệm thu / tiểu ban).
  const normalisedType = type === "conference" ? "council" : type;

  const buttons = getButtonsFor(lastValue, normalisedType, row?.durationMonths, row);
  return {
    status: [{ value: lastValue, label, bgcolor }],
    buttons,
  };
};
